import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useSearchParams } from "react-router-dom";
import {
  PERMISSION_PROJECT_OPTIONS,
  PermissionProject,
} from "../../../../../models/Employees/PermissionProject";
import { useStores } from "../../../use-stores";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import ActivityLogDrawer from "../../../../../views/components/common/drawer/ActivityLogDrawer";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import {
  getModernPageColors,
  modernPageCardSx,
  modernPageShellSx,
} from "../../../../../styles/modernListingPageSx";
import { TeamPermissionStore } from "../../../stores/TeamPermissionStore";
import TeamPermissionFilters from "../../components/employee/TeamPermissionFilters";
import TeamPermissionMatrix from "../../components/employee/TeamPermissionMatrix";

const TeamPermissionsPage = () => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { userStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryRef = useRef(readTeamPermissionQuery(searchParams));
  const storeRef = useRef<TeamPermissionStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = new TeamPermissionStore();
    storeRef.current.hydrateFilters(initialQueryRef.current);
  }
  const store = storeRef.current;

  const [activityOpen, setActivityOpen] = useState(false);
  const debouncedSearch = useDebounce(store.searchDraft, 400);
  const canView = userStore.hasPermission(EmployeeRoles.ALL);

  useEffect(() => {
    if (store.searchText === debouncedSearch) return;
    store.setSearchText(debouncedSearch);
  }, [debouncedSearch, store, store.searchText]);

  useEffect(() => {
    const nextQuery = {
      project: store.project,
      search: store.searchText.trim(),
      teamIds: store.selectedTeamIds,
      permissionIds: store.selectedPermissionIds,
    };
    if (sameTeamPermissionQuery(readTeamPermissionQuery(searchParams), nextQuery)) {
      return;
    }
    setSearchParams(writeTeamPermissionQuery(nextQuery), { replace: true });
  }, [
    store.project,
    store.searchText,
    store.selectedTeamIds,
    store.selectedPermissionIds,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!canView) return;
    void store.load();
  }, [canView, store]);

  const openActivity = async () => {
    await store.loadActivityLogs();
    if (!store.activityError) setActivityOpen(true);
  };

  useEffect(() => {
    setActivityOpen(false);
  }, [store.project]);

  useEffect(() => {
    if (!store.savedMessage) return;
    const timer = window.setTimeout(() => {
      store.clearSavedMessage();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [store, store.savedMessage]);

  if (!canView) {
    return (
      <Box sx={modernPageShellSx(theme)}>
        <PermissionBlockLabel title="You need the All permission to view or change team permissions." />
      </Box>
    );
  }

  return (
    <Box sx={modernPageShellSx(theme)}>
      <Paper sx={modernPageCardSx(theme)}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <CustomTypography variant="heading3" color={colors.title}>
            Team permissions
          </CustomTypography>
          <Tooltip title="Activity history">
            <IconButton
              aria-label="Open team permission activity history"
              disabled={store.activityLoading}
              onClick={() => void openActivity()}
              sx={{ color: colors.accent }}
            >
              {store.activityLoading ? (
                <CircularProgress size={20} />
              ) : (
                <HistoryIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <CustomTypography variant="body2" color={colors.subtitle}>
          Check a permission in the first column to unlock that row. Change the
          teams on unlocked rows, then save. Locked rows stay as they are.
        </CustomTypography>
        {store.activityError && (
          <Box sx={{ mt: 1 }}>
            <InlineFormError message={store.activityError} />
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <TeamPermissionFilters store={store} />
        </Box>

        {store.error && (
          <Box sx={{ mt: 2 }}>
            <InlineFormError message={store.error} />
            <AppButton
              variant="transparentPrimary"
              label="Try again"
              onClick={() => void store.load()}
            />
          </Box>
        )}

        {!store.error &&
          (store.saveError ||
            store.savedMessage ||
            store.hasUnsavedChanges()) && (
            <Box sx={{ mt: 1.5 }}>
              {store.saveError && <InlineFormError message={store.saveError} />}
              {store.savedMessage && (
                <CustomTypography variant="body2" color={colors.success}>
                  {store.savedMessage}
                </CustomTypography>
              )}
              {store.hasUnsavedChanges() && !store.savedMessage && (
                <CustomTypography variant="body2" color={colors.subtitle}>
                  You have unsaved checkbox changes.
                </CustomTypography>
              )}
            </Box>
          )}

        <Box sx={{ mt: 3, position: "relative", minHeight: 160 }}>
          {store.loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
          {!store.loading && !store.error && (
            <TeamPermissionMatrix store={store} />
          )}
        </Box>
        <ActivityLogDrawer
          isOpen={activityOpen}
          onClose={() => setActivityOpen(false)}
          activityLogs={store.activityLogs.map((log) => ({
            id: log.id,
            activity: log.activity,
            created_by_name: log.adminName,
            created_at: log.createdAt,
          }))}
          isLoading={store.activityLoading}
          title="Activity history"
          subtitle={
            PERMISSION_PROJECT_OPTIONS.find((option) => option.id === store.project)
              ?.value
          }
        />
      </Paper>
    </Box>
  );
};

type TeamPermissionQuery = {
  project: PermissionProject;
  search: string;
  teamIds: number[];
  permissionIds: number[];
};

function parseIdList(value: string | null): number[] {
  if (!value) return [];
  const ids: number[] = [];
  const seen = new Set<number>();
  value.split(",").forEach((part) => {
    const id = Number(part.trim());
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

function readTeamPermissionQuery(params: URLSearchParams): TeamPermissionQuery {
  const project = params.get("project") === "swayy" ? "swayy" : "swageazy";
  return {
    project,
    search: (params.get("search") || "").trim(),
    teamIds: parseIdList(params.get("team_ids")),
    permissionIds: parseIdList(params.get("permission_ids")),
  };
}

function sameIdList(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
}

function sameTeamPermissionQuery(
  left: TeamPermissionQuery,
  right: TeamPermissionQuery,
): boolean {
  return (
    left.project === right.project &&
    left.search === right.search &&
    sameIdList(left.teamIds, right.teamIds) &&
    sameIdList(left.permissionIds, right.permissionIds)
  );
}

function writeTeamPermissionQuery(query: TeamPermissionQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.project === "swayy") params.set("project", "swayy");
  if (query.search) params.set("search", query.search);
  if (query.teamIds.length > 0) params.set("team_ids", query.teamIds.join(","));
  if (query.permissionIds.length > 0) {
    params.set("permission_ids", query.permissionIds.join(","));
  }
  return params;
}

export default observer(TeamPermissionsPage);
